/**
 * Adapters from the generated tile contract to the drill-in's view models:
 * the loop chart, the optimization-funnel context, and the reports-console
 * base filters. Kept out of the page component so the page stays declarative
 * and these stay unit-testable.
 */

import type { ReportFilterSpec } from '@features/reports';

import type {
  SemanticMetricTilePayload,
  SemanticTileDrilldown,
} from '../types';

import type { MetricLoopCardData } from './MetricLoopCard';
import type { OptimizationMetricContext } from './OptimizationModal';

const prettyKind = (kind?: string | null): string | undefined =>
  kind ? kind.replace(/_/g, ' ').replace(/\b\w/, c => c.toUpperCase()) : undefined;

/** Formatted headline value, e.g. "51%" (ratio) or "4" (count); "—" when gated. */
export const formatValue = (p: SemanticMetricTilePayload): string => {
  if (!p.meets_gate) return '—';
  if (p.value_pct != null) return `${Math.round(p.value_pct)}%`;
  if (p.value_count != null) return String(p.value_count);
  return '—';
};

/** The annotated loop chart from the tile's trend + first intervention marker. */
export const loopFromPayload = (p: SemanticMetricTilePayload): MetricLoopCardData => {
  const series = (p.trend ?? [])
    .filter((pt): pt is { x: string; y: number; n?: number | null } =>
      pt.x != null && pt.y != null,
    )
    .map(pt => ({ x: pt.x, y: pt.y }));
  const markers = (p.markers ?? [])
    .filter(m => m.occurred_at)
    .map(m => ({ x: m.occurred_at as string, label: m.label ?? 'Optimization' }));
  const delta = p.delta_since_lever;
  return {
    title: p.display_name ?? p.slug,
    subtitle: p.applies_to_label ?? undefined,
    series,
    markers,
    deltaLabel:
      delta != null && delta !== 0
        ? `${delta > 0 ? '+' : ''}${Math.round(delta)} pts since lever`
        : undefined,
    nCaption: p.n != null ? `n=${p.n} · trailing 90 days` : undefined,
  };
};

/** Context for the optimization funnel opened by the lever CTA. */
export const optimizationContextFromPayload = (
  p: SemanticMetricTilePayload,
): OptimizationMetricContext => ({
  name: p.display_name ?? p.slug,
  appliesTo: p.applies_to_label ?? '',
  standing: formatValue(p),
  standingCaption: p.meets_gate
    ? `n=${p.n ?? 0} in the trailing 90 days`
    : `Below the ${p.min_denominator ?? 15}-sample gate (n=${p.n ?? 0})`,
  fix: p.lever?.target ?? p.lever?.label ?? 'a targeted template change',
  fixKind: prettyKind(p.lever?.kind),
});

/** The drilldown's locked filters as reports-engine filter specs. */
export const drilldownBaseFilters = (
  d: SemanticTileDrilldown | null | undefined,
): ReportFilterSpec[] =>
  (d?.filters ?? []).map(f => ({ field: f.field, op: f.op, values: f.values ?? [] }));
